import { Link } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { ChevronLeft, FileText } from 'lucide-react';
import SiteHeaderV2 from '@/components/website/SiteHeaderV2';
import SiteFooterV2 from '@/components/website/SiteFooterV2';
import PageTitle from '@/components/website/PageTitle';

/* eslint-disable react/no-unescaped-entities */

const RespiratoryMechanicsPage = () => {

  const noseFunctions = [
    {
      title: "Filtration",
      description: "Les poils et le mucus nasal filtrent les particules, poussières, allergènes et microbes avant qu'ils n'atteignent les poumons."
    },
    {
      title: "Humidification",
      description: "L'air inspiré est humidifié à 80-90% d'humidité relative, protégeant ainsi les alvéoles pulmonaires du dessèchement."
    },
    {
      title: "Réchauffement",
      description: "L'air est réchauffé à température corporelle (37°C), évitant le choc thermique des voies respiratoires."
    },
    {
      title: "Production de NO",
      description: "Les sinus produisent de l'oxyde nitrique (NO), un puissant bronchodilatateur et antimicrobien qui améliore l'oxygénation de 10-15%."
    },
    {
      title: "Résistance optimale",
      description: "La résistance nasale stimule correctement le diaphragme et maintient un rythme respiratoire optimal."
    },
    {
      title: "Olfaction",
      description: "Détection des odeurs qui influence l'appétit, les émotions et la mémoire."
    }
  ];

  const diaphragmImpacts = [
    {
      title: "Ventilation Efficace",
      description: "Le diaphragme assure 70-80% du travail respiratoire. Il permet une ventilation plus efficace avec moins d'effort que la respiration thoracique haute."
    },
    {
      title: "Système Nerveux Autonome",
      description: "La respiration diaphragmatique active le nerf vague, stimulant le système parasympathique (relaxation) et réduisant le stress."
    },
    {
      title: "Massage Viscéral",
      description: "Chaque inspiration descend le diaphragme de 1-10 cm, massant les organes abdominaux (foie, estomac, intestins) et améliorant la digestion."
    },
    {
      title: "Retour Veineux",
      description: "Le mouvement du diaphragme agit comme une pompe, facilitant le retour veineux et lymphatique vers le cœur."
    },
    {
      title: "Posture et Stabilité",
      description: "Le diaphragme est un muscle postural clé, travaillant avec les muscles du plancher pelvien et les abdominaux pour stabiliser la colonne."
    },
    {
      title: "Pression Intra-abdominale",
      description: "Régulation de la pression abdominale essentielle pour la continence, le support des organes et la protection lombaire."
    },
    {
      title: "Oxygénation Optimale",
      description: "La respiration diaphragmatique ventile mieux les bases pulmonaires (zones les plus vascularisées), optimisant les échanges gazeux."
    }
  ];

  return (
    <div 
      className="min-h-screen" 
      style={{ backgroundColor: '#FFFFFF', fontFamily: 'Figtree, sans-serif' }}
    >
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Outfit:wght@300;400;500&family=Figtree:wght@400;500;600&display=swap');
      `}</style>
      
      <SiteHeaderV2 />
      
      {/* Hero Section */}
      <section className="py-16 lg:py-24" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-7xl mx-auto px-6 sm:px-12">
          <div className="max-w-6xl mx-auto">
            <PageTitle
              kicker="Comprendre"
              title="Mécanique"
              highlight="Respiratoire"
              subtitle="Comprendre l'anatomie et la physiologie de la respiration pour optimiser votre santé."
              align="center"
            />
          </div>
        </div>
      </section>

      {/* Video Section - La mécanique respiratoire optimale */}
      <section className="py-20">
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
            >
              La Mécanique Respiratoire <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Optimale</span>
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Découvrez les principes essentiels d'une respiration optimale
            </p>
          </div>

          <div 
            className="rounded-3xl overflow-hidden shadow-lg"
            style={{ backgroundColor: '#FFFFFF', border: '1px solid #F8FAFC' }}
          >
            <div className="p-6 md:p-8">
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: '56.25%', maxWidth: '800px', margin: '0 auto' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/WAJkNpz1Lg0"
                  title="La Mécanique Respiratoire Optimale"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Animation Section */}
      <section className="py-20" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-5xl mx-auto px-6 sm:px-12">
          <div className="text-center mb-12">
            <h2 
              className="text-3xl sm:text-4xl mb-4"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
            >
              Le Diaphragme <span style={{ color: '#0EA5E9', fontWeight: 400 }}>en Action</span>
            </h2>
            <p className="max-w-2xl mx-auto" style={{ color: '#475569' }}>
              Visualisez le mouvement réel du diaphragme pendant la respiration
            </p>
          </div>

          <div 
            className="rounded-3xl overflow-hidden shadow-lg"
            style={{ backgroundColor: '#FFFFFF' }}
          >
            <div className="p-6 md:p-8">
              <div className="relative w-full rounded-2xl overflow-hidden" style={{ paddingBottom: '56.25%', maxWidth: '800px', margin: '0 auto' }}>
                <iframe
                  className="absolute top-0 left-0 w-full h-full"
                  src="https://www.youtube.com/embed/TzuTzLvmiTM"
                  title="Mouvement du Diaphragme"
                  frameBorder="0"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                  allowFullScreen
                />
              </div>
              
              <div 
                className="mt-8 p-6 rounded-2xl"
                style={{ backgroundColor: '#E0F2FE' }}
              >
                <h3 className="text-lg font-medium mb-4" style={{ color: '#0F172A' }}>Observation du Mouvement</h3>
                <div className="space-y-3 text-sm" style={{ color: '#475569' }}>
                  <p><strong style={{ color: '#0F172A' }}>INSPIRATION :</strong> Le diaphragme descend et s'aplatit, créant une dépression qui permet aux poumons de se remplir d'air.</p>
                  <p><strong style={{ color: '#0F172A' }}>EXPIRATION :</strong> Le diaphragme remonte et reprend sa forme de dôme concave, expulsant l'air des poumons.</p>
                  <p className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(14,165,233,0.2)' }}>
                    <strong style={{ color: '#0EA5E9' }}>Le diaphragme assure 70-80% du travail respiratoire</strong> au repos. Une respiration diaphragmatique correcte est essentielle pour une santé optimale.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Diaphragm Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <h2 
            className="text-3xl sm:text-4xl mb-8"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
          >
            Le Diaphragme : Bien Plus qu'un <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Muscle Respiratoire</span>
          </h2>
          
          <div className="space-y-6" style={{ color: '#475569' }}>
            <p className="text-lg leading-relaxed">
              Le diaphragme est le muscle le plus important du corps humain. En forme de dôme, il sépare 
              la cavité thoracique de la cavité abdominale et s'abaisse lors de l'inspiration, créant une 
              dépression qui permet aux poumons de se remplir d'air.
            </p>
            <p className="text-lg leading-relaxed mb-4">
              Au repos, nous respirons environ <strong>15 000 fois par jour</strong>. Si le diaphragme ne 
              fonctionne pas correctement, ce sont les muscles accessoires (cou, épaules) qui doivent compenser, 
              entraînant tensions, fatigue et multiples dysfonctionnements.
            </p>
          </div>

          <div className="space-y-4">
            {diaphragmImpacts.map((impact, index) => (
              <div 
                key={index} 
                className="p-6 rounded-2xl transition-all hover:shadow-md"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #F8FAFC' }}
              >
                <div className="flex items-start gap-4">
                  <div 
                    className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                    style={{ backgroundColor: '#E0F2FE' }}
                  >
                    <span className="font-medium" style={{ color: '#0EA5E9' }}>{index + 1}</span>
                  </div>
                  <div>
                    <h3 className="text-lg font-medium mb-2" style={{ color: '#0F172A' }}>{impact.title}</h3>
                    <p style={{ color: '#475569' }}>{impact.description}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>

          <div 
            className="mt-10 p-6 rounded-2xl"
            style={{ backgroundColor: '#E0F2FE' }}
          >
            <h4 className="font-medium mb-4" style={{ color: '#0F172A' }}>Comment Savoir si Votre Diaphragme Fonctionne Bien ?</h4>
            <div className="space-y-3" style={{ color: '#475569' }}>
              <p><strong style={{ color: '#0F172A' }}>Test simple :</strong> Allongez-vous sur le dos, placez une main sur votre poitrine et l'autre sur votre ventre.</p>
              <p>Lors de l'inspiration :</p>
              <ul className="ml-6 space-y-1">
                <li><strong style={{ color: '#0EA5E9' }}>Bon :</strong> Le ventre se soulève en premier, la poitrine bouge peu</li>
                <li><strong style={{ color: '#475569' }}>Dysfonctionnel :</strong> La poitrine se soulève en premier, le ventre bouge peu ou rentre</li>
              </ul>
              <p className="mt-4 pt-4" style={{ borderTop: '1px solid rgba(14,165,233,0.2)' }}>
                Si votre respiration est principalement thoracique, les exercices de rééducation respiratoire 
                peuvent vous aider à retrouver une respiration diaphragmatique optimale.
              </p>
            </div>
          </div>

          {/* Additional Help Section with Video */}
          <div 
            className="mt-10 p-6 rounded-2xl"
            style={{ backgroundColor: '#F8FAFC', borderLeft: '4px solid #0EA5E9' }}
          >
            <p className="mb-6" style={{ color: '#475569' }}>
              Si vous respirez par le thorax, les épaules, le cou, et que vous n'arrivez pas à ressentir l'activation 
              de votre diaphragme, ou même simplement comment relâcher vos abdominaux, vous pouvez commencer par 
              l'exercice ci-dessous. Par la suite, référez-vous à la vidéo au sommet de la page pour progresser vers 
              la "vraie, bonne respiration".
            </p>

            {/* Exercise Video */}
            <div 
              className="rounded-2xl overflow-hidden shadow-md"
              style={{ backgroundColor: '#FFFFFF' }}
            >
              <div className="p-4">
                <div className="relative w-full rounded-xl overflow-hidden" style={{ paddingBottom: '56.25%' }}>
                  <iframe
                    className="absolute top-0 left-0 w-full h-full"
                    src="https://www.youtube.com/embed/npENP7-rOOs"
                    title="Exercice de respiration diaphragmatique"
                    frameBorder="0"
                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                    allowFullScreen
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Nasal Breathing Section */}
      <section className="py-20" style={{ backgroundColor: '#F8FAFC' }}>
        <div className="max-w-4xl mx-auto px-6 sm:px-12">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-8">
            <h2 
              className="text-3xl sm:text-4xl"
              style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
            >
              L'Importance de la <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Respiration Nasale</span>
            </h2>
            <a
              href="/documents/Les_Trente_Fonctions_du_Nez.pptx"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full font-medium transition-all hover:shadow-lg"
              style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF' }}
              data-testid="thirty-functions-button"
            >
              <FileText size={18} />
              Les 30 fonctions du nez
            </a>
          </div>
          
          <div className="space-y-4 mb-10" style={{ color: '#475569' }}>
            <p className="text-lg leading-relaxed">
              La respiration nasale n'est pas qu'une simple préférence - c'est une nécessité physiologique. 
              Nos voies nasales sont conçues comme un système de traitement d'air sophistiqué qui prépare 
              optimalement l'air avant qu'il n'atteigne les poumons.
            </p>
            <p className="text-lg leading-relaxed">
              <strong style={{ color: '#0F172A' }}>Malheureusement, 30-50% de la population respire chroniquement par la bouche</strong>, 
              soit par habitude, soit par obstruction nasale, privant leur organisme de multiples bénéfices 
              physiologiques essentiels.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            {noseFunctions.map((func, index) => (
              <div 
                key={index} 
                className="p-6 rounded-2xl transition-all hover:shadow-md"
                style={{ backgroundColor: '#FFFFFF', border: '1px solid #F8FAFC' }}
              >
                <h3 className="text-lg font-medium mb-2" style={{ color: '#0EA5E9' }}>{func.title}</h3>
                <p style={{ color: '#475569' }}>{func.description}</p>
              </div>
            ))}
          </div>

          <div 
            className="mt-10 p-6 rounded-2xl"
            style={{ backgroundColor: '#E0F2FE', borderLeft: '4px solid #0EA5E9' }}
          >
            <h4 className="font-medium mb-4" style={{ color: '#0F172A' }}>Conséquences de la Respiration Buccale Chronique</h4>
            <ul className="space-y-2" style={{ color: '#475569' }}>
              <li>• Dessèchement des muqueuses et risque accru d'infections</li>
              <li>• Hyperventilation et déséquilibre du CO2 sanguin</li>
              <li>• Mauvais développement cranio-facial chez l'enfant</li>
              <li>• Apnées du sommeil et ronflements</li>
              <li>• Fatigue chronique et baisse de performance</li>
              <li>• Mauvaise haleine et problèmes dentaires</li>
            </ul>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6 sm:px-12 text-center">
          <h2 
            className="text-3xl sm:text-4xl mb-6"
            style={{ fontFamily: 'Outfit, sans-serif', fontWeight: 300, color: '#0F172A' }}
          >
            Optimisez Votre <span style={{ color: '#0EA5E9', fontWeight: 400 }}>Respiration</span>
          </h2>
          <p className="text-lg mb-10 max-w-2xl mx-auto" style={{ color: '#475569' }}>
            Apprenez à respirer correctement avec nos exercices guidés et retrouvez une fonction 
            respiratoire optimale.
          </p>
          <Link to="/app-landing">
            <button 
              className="px-10 py-4 text-lg rounded-full font-medium transition-all hover:shadow-lg hover:-translate-y-1"
              style={{ backgroundColor: '#0EA5E9', color: '#FFFFFF' }}
            >
              Commencer les exercices
            </button>
          </Link>
        </div>
      </section>

      <SiteFooterV2 />
    </div>
  );
};

export default RespiratoryMechanicsPage;
